import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { isTaskTodo } from '@mosey/utils/tasks';
import { Button } from '@mosey/components/buttons/Button';
import {
  useNextTaskUrl,
  useResolverFinishUrl,
  useTasks,
  useTasksFrameworkLocation,
  useTodoTasks,
} from '../utils/hooks';
import { ResolverType, LocationResolverRouteParams } from '../utils/types';
import { FullHeightWrapper } from '../common/Interstitial';
import { AssessmentInterstitial } from './AssessmentInterstitial';
import { SetupInterstitial } from './SetupInterstitial';
import { useIsPlatformUser, usePlatform } from '../../../hooks/useUser';
import { WizardInterstitial } from '../wizard/WizardInterstitial';
import noTasksGraphic from '../../../assets/tasks-framework/tasks-overview-wizard-automated-zero.svg';

export const ResolverIntro = () => {
  const { resolverType = ResolverType.Assessment } =
    useParams<LocationResolverRouteParams>();
  const [searchParams] = useSearchParams();
  const isPlatformUser = useIsPlatformUser();
  const platform = usePlatform();
  const tasks = useTasks();
  const todoTasks = useTodoTasks();
  const nextTaskUrl = useNextTaskUrl();
  const location = useTasksFrameworkLocation();
  const skip = searchParams.get('skip');
  const resolverFinishUrl = useResolverFinishUrl();

  if (tasks.length === 0) {
    if (isPlatformUser) {
      return (
        // TODO: Rename this if we like using this
        <WizardInterstitial
          title="No action required"
          body="There are no tasks for you to complete."
          graphic={<img src={noTasksGraphic} alt="" />}
          actions={
            <Button
              as={Link}
              size="xlarge"
              to={resolverFinishUrl}
              state={{ fullWidthPlatformAppChrome: true }}
            >
              {platform?.name ? `Return to ${platform.name}` : 'Finish'}
            </Button>
          }
        />
      );
    }

    return (
      <Navigate
        to={resolverFinishUrl}
        replace
        state={{ fullWidthPlatformAppChrome: true }}
      />
    );
  }

  if (resolverType === ResolverType.Review) {
    const nextReviewTask = tasks.find(isTaskTodo) ?? tasks[0];
    const nextReviewUrl = `tasks/${nextReviewTask.id}`;
    return <Navigate to={nextReviewUrl} replace />;
  }

  if (
    (!location ||
      (resolverType !== ResolverType.Assessment &&
        resolverType !== ResolverType.Setup) ||
      todoTasks.length < tasks.length ||
      skip) &&
    nextTaskUrl
  ) {
    return <Navigate to={nextTaskUrl} replace />;
  }

  if (todoTasks.length === 0) {
    return <Navigate to="complete" replace />;
  }

  if (tasks.length === 1) {
    return <Navigate to={`tasks/${tasks[0].id}`} replace />;
  }

  const percentComplete =
    (resolverType === ResolverType.Assessment ? 1 / 3 : 2 / 3) * 100;

  return (
    <FullHeightWrapper>
      {resolverType === ResolverType.Assessment ? (
        <AssessmentInterstitial percentComplete={percentComplete} />
      ) : (
        <SetupInterstitial percentComplete={percentComplete} />
      )}
    </FullHeightWrapper>
  );
};
